import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import SiteLink from './SiteLink'
import { motion, AnimatePresence } from 'framer-motion'

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const location = useLocation()

  const navigation = [
    { name: 'Home', href: '/' },
  ]

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  // Close mobile menu when route changes and scroll to top
  useEffect(() => {
    setIsMenuOpen(false)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [location])

  const isActivePath = (path: string) => {
    if (path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(path)
  }

  return (
    <header
      className={`font-outfit fixed top-0 left-0 right-0 border-b border-ink-green-edge dark:border-ink-green-edge transition-colors duration-300 ${
        isScrolled
          ? 'bg-ink-green-canvas dark:bg-ink-green-canvas'
          : 'bg-ink-green-canvas dark:bg-ink-green-canvas'
      }`}
      style={{ zIndex: 9999, position: 'fixed' }}
    >
      <nav className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 overflow-visible" style={{ position: 'relative', zIndex: 10 }}>
        <div className="flex items-center justify-between h-16 sm:h-18 md:h-20">
          {/* Wordmark */}
          <SiteLink
            href="/"
            className="flex items-center min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-green-accent focus-visible:ring-offset-2 focus-visible:ring-offset-ink-green-canvas"
          >
            <span className="text-base sm:text-xl md:text-2xl font-medium tracking-[-.03em] text-ink-green-ink dark:text-ink-green-ink whitespace-nowrap">
              novahass<span className="text-ink-green-accent">.</span>partners
            </span>
          </SiteLink>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navigation.map((item) => (
              <SiteLink
                key={item.name}
                href={item.href}
                className={`px-2 lg:px-2 xl:px-3 py-2 min-h-[44px] inline-flex items-center text-xs xl:text-sm transition-colors duration-300 rounded-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-green-accent focus-visible:ring-offset-2 focus-visible:ring-offset-ink-green-canvas ${
                  isActivePath(item.href)
                    ? 'text-ink-green-accent'
                    : 'text-ink-green-ink-soft dark:text-ink-green-ink-soft hover:text-ink-green-ink dark:hover:text-ink-green-ink'
                }`}
              >
                {item.name}
              </SiteLink>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center">
            <SiteLink
              href="/"
              className="inline-flex min-h-[44px] items-center px-1 text-xs xl:text-sm font-medium text-ink-green-ink dark:text-ink-green-ink underline decoration-1 underline-offset-[.24em] hover:text-ink-green-accent transition-colors duration-300 whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-green-accent focus-visible:ring-offset-2 focus-visible:ring-offset-ink-green-canvas"
            >
              Get in touch
            </SiteLink>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center">
            <button
              type="button"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="relative p-2 min-h-[44px] min-w-[44px] transition-all duration-300 rounded-control border border-ink-green-edge hover:border-ink-green-accent bg-ink-green-canvas dark:bg-ink-green-canvas overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-green-accent focus-visible:ring-offset-2 focus-visible:ring-offset-ink-green-canvas"
              style={{ position: 'relative', zIndex: 100 }}
              aria-label="Toggle menu"
            >
              <div className="relative z-10 w-5 h-5 flex flex-col justify-center items-center gap-1">
                <span
                  className={`absolute block w-5 h-0.5 bg-ink-green-ink dark:bg-ink-green-ink transition-all duration-700 ease-out ${
                    isMenuOpen ? 'w-4' : 'static w-5'
                  }`}
                  style={isMenuOpen ? { top: '50%', left: '50%', transform: 'translate(-50%, -50%) rotate(0deg)' } : {}}
                />
                <span
                  className={`absolute block w-5 h-0.5 bg-ink-green-ink dark:bg-ink-green-ink transition-all duration-700 ease-out ${
                    isMenuOpen ? 'w-4' : 'static w-5'
                  }`}
                  style={isMenuOpen ? { top: '50%', left: '50%', transform: 'translate(-50%, -50%) rotate(120deg)' } : {}}
                />
                <span
                  className={`absolute block w-5 h-0.5 bg-ink-green-ink dark:bg-ink-green-ink transition-all duration-700 ease-out ${
                    isMenuOpen ? 'w-4' : 'static w-5'
                  }`}
                  style={isMenuOpen ? { top: '50%', left: '50%', transform: 'translate(-50%, -50%) rotate(240deg)' } : {}}
                />
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="lg:hidden absolute top-full right-2 mt-2 w-64 sm:w-80"
              style={{ zIndex: 10000 }}
            >
              <div className="relative bg-ink-green-canvas dark:bg-ink-green-canvas px-4 py-4 space-y-2 border border-ink-green-edge rounded-panel overflow-hidden">
                {navigation.map((item, index) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="relative z-10"
                  >
                    <SiteLink
                      href={item.href}
                      className={`block px-3 py-2 min-h-[44px] flex items-center text-sm transition-colors duration-300 rounded-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-green-accent focus-visible:ring-offset-2 focus-visible:ring-offset-ink-green-canvas ${
                        isActivePath(item.href)
                          ? 'text-ink-green-accent'
                          : 'text-ink-green-ink-soft dark:text-ink-green-ink-soft hover:text-ink-green-ink dark:hover:text-ink-green-ink'
                      }`}
                    >
                      {item.name}
                    </SiteLink>
                  </motion.div>
                ))}

                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: navigation.length * 0.1 }}
                  className="relative z-10 pt-3 mt-2 border-t border-ink-green-edge"
                >
                  <SiteLink
                    href="/"
                    className="block px-4 py-3 min-h-[44px] text-sm font-medium text-ink-green-ink dark:text-ink-green-ink underline decoration-1 underline-offset-[.24em] text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-green-accent focus-visible:ring-offset-2 focus-visible:ring-offset-ink-green-canvas"
                  >
                    Get in touch
                  </SiteLink>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  )
}

export default Header
